import React, { useState, useRef } from 'react';

const ElectronicPrescriptionPDF = ({ 
    prescriptionData, 
    selectedPatient, 
    selectedDoctor,
    medicationDetails,
    prescriptionConfig,
    onClose 
}) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const pdfRef = useRef();

    // Función para generar y descargar el PDF
    const generatePDF = async () => {
        setIsGenerating(true);
        try {
            // Cargar html2pdf dinámicamente desde CDN
            if (!window.html2pdf) {
                const script = document.createElement('script');
                script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
                script.onload = () => {
                    // Esperamos un poco para que la librería se inicialice
                    setTimeout(downloadPDF, 500);
                };
                document.head.appendChild(script);
            } else {
                // Esperamos un poco para asegurar que el contenido esté renderizado
                setTimeout(downloadPDF, 100);
            }
        } catch (error) {
            console.error('Error generando PDF:', error);
            alert('Error al generar el PDF');
            setIsGenerating(false);
        }
    };

    const downloadPDF = async () => {
        const element = pdfRef.current;
        
        if (!element) {
            console.error('Elemento no encontrado para generar PDF');
            setIsGenerating(false);
            return;
        }

        const prescriptionId = prescriptionData?.prescriptionId || '00000';
        const patientName = selectedPatient?.name && selectedPatient?.surname 
            ? `${selectedPatient.name}_${selectedPatient.surname}`
            : 'Paciente';

        // Configuración optimizada para html2pdf
        const opt = {
            margin: [5, 5, 5, 5], // Márgenes más pequeños
            filename: `Receta_MPR${prescriptionId}_${patientName}_${new Date().toISOString().split('T')[0]}.pdf`,
            image: { 
                type: 'jpeg', 
                quality: 0.98 
            },
            html2canvas: { 
                scale: 1.5, // Reducido para mejor rendimiento
                useCORS: true,
                allowTaint: false, // Cambiar a false
                letterRendering: true,
                logging: true, // Activar para debug
                width: element.scrollWidth,
                height: element.scrollHeight,
                scrollX: 0,
                scrollY: 0
            },
            jsPDF: { 
                unit: 'mm', 
                format: 'a4', 
                orientation: 'landscape',
                compress: true
            }
        };

        try {
            console.log('Iniciando generación de PDF...');
            console.log('Elemento:', element);
            console.log('Dimensiones:', element.offsetWidth, 'x', element.offsetHeight);
            
            await window.html2pdf()
                .set(opt)
                .from(element)
                .save();
                
            console.log('PDF descargado correctamente');
            setIsGenerating(false);
        } catch (error) {
            console.error('Error al descargar PDF:', error);
            alert('Error al descargar el PDF: ' + error.message);
            setIsGenerating(false);
        }
    };

    // Formatear fecha para mostrar
    const formatDate = (date) => {
        if (!date) return '';
        try {
            return new Date(date).toLocaleDateString('es-ES');
        } catch {
            return '';
        }
    };

    // Obtener datos del paciente
    const patientName = selectedPatient?.name && selectedPatient?.surname 
        ? `${selectedPatient.name} ${selectedPatient.surname}`
        : prescriptionData?.patientInfo?.fullName || '';

    // Obtener datos del doctor
    const doctorName = selectedDoctor?.name && selectedDoctor?.surname
        ? `${selectedDoctor.name} ${selectedDoctor.surname}`
        : prescriptionData?.prescriberInfo?.fullName || '';

    // Obtener datos del medicamento
    const medicationName = medicationDetails?.nombre || prescriptionData?.medication?.nombre || '';
    const activeIngredient = medicationDetails?.principioActivo || prescriptionData?.medication?.principioActivo || '';
    const pharmaceuticalForm = medicationDetails?.formaFarmaceutica || prescriptionData?.medication?.formaFarmaceutica || '';
    const dosage = medicationDetails?.dosisUnidad || prescriptionData?.medication?.dosisUnidad || '';
    const administration = medicationDetails?.viaAdministracion || prescriptionData?.medication?.viaAdministracion || '';
    const laboratory = medicationDetails?.laboratorio || prescriptionData?.medication?.laboratorio || '';

    // Obtener configuración de la prescripción
    const posology = prescriptionConfig?.posology || prescriptionData?.posology || {};
    const treatmentDuration = prescriptionConfig?.treatmentDuration || prescriptionData?.treatmentDuration || {};
    const numberOfPackages = prescriptionConfig?.numberOfPackages || prescriptionData?.numberOfPackages || 1;
    const specialInstructions = prescriptionConfig?.specialInstructions || prescriptionData?.specialInstructions || '';

    // Obtener ID de prescripción
    const prescriptionId = prescriptionData?.prescriptionId || '00000';

    // Estilos optimizados para PDF
    const customStyles = `
        .pdf-content {
            width: 297mm !important;
            min-height: 210mm !important;
            padding: 10mm !important;
            margin: 0 !important;
            background: white !important;
            font-family: Arial, sans-serif !important;
            box-sizing: border-box !important;
            position: relative !important;
        }
        
        .prescription-table {
            border-collapse: collapse !important;
            width: 100% !important;
            border: 1px solid black !important;
            font-family: Arial, sans-serif !important;
            font-size: 9px !important;
            table-layout: fixed !important;
        }
        
        .prescription-table td, .prescription-table th {
            border: 1px solid black !important;
            padding: 6px !important;
            vertical-align: top !important;
            line-height: 1.3 !important;
            word-wrap: break-word !important;
        }
        
        .prescription-table .thick-border {
            border: 1px solid black !important;
        }
        
        .prescription-table .thick-border-right {
            border-right: 1px solid black !important;
        }
        
        .prescription-table .thick-border-bottom {
            border-bottom: 1px solid black !important;
        }
        
        .prescription-table .bg-light {
            background-color: #f8f9fa !important;
        }
        
        .vertical-text {
            writing-mode: vertical-rl !important;
            text-orientation: mixed !important;
            font-weight: bold !important;
            font-size: 10px !important;
            text-align: center !important;
            letter-spacing: 1px !important;
            text-transform: uppercase !important;
        }
        
        .section-title {
            font-weight: bold !important;
            font-size: 10px !important;
            text-align: center !important;
            margin-bottom: 5px !important;
        }
        
        .section-description {
            font-size: 7px !important;
            text-align: center !important;
            color: #666 !important;
            line-height: 1.2 !important;
            margin-bottom: 8px !important;
        }
        
        .checkbox-item {
            display: flex !important;
            align-items: center !important;
            gap: 3px !important;
            font-size: 8px !important;
            margin-bottom: 3px !important;
        }
        
        .checkbox-item input {
            width: 10px !important;
            height: 10px !important;
        }
        
        .mpr-number {
            font-size: 16px !important;
            font-weight: bold !important;
            text-align: center !important;
        }
        
        .data-row {
            font-size: 9px !important;
            margin-bottom: 3px !important;
        }
        
        .package-box {
            border: 1px solid black !important;
            width: 30px !important;
            height: 25px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            font-weight: bold !important;
            font-size: 12px !important;
            margin-left: 10px !important;
            background: white !important;
        }
        
        .legal-text {
            font-size: 7px !important;
            line-height: 1.2 !important;
            text-align: justify !important;
        }

        .medication-details {
            font-size: 9px !important;
            line-height: 1.3 !important;
        }

        .header-logos {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            margin-bottom: 10px !important;
        }

        .header-logos img {
            max-height: 40px !important;
            max-width: 100% !important;
        }

        .main-title {
            border: 1px solid black !important;
            background-color: #f8f9fa !important;
            text-align: center !important;
            font-weight: bold !important;
            text-transform: uppercase !important;
            padding: 8px !important;
            font-size: 12px !important;
            margin-bottom: 5px !important;
        }

        /* Asegurar que las imágenes se muestren correctamente */
        img {
            max-width: 100% !important;
            height: auto !important;
            display: block !important;
        }
    `;

    return (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" 
             style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)', zIndex: 9999 }}>
            <div className="bg-white rounded shadow-lg" style={{ maxWidth: '95vw', maxHeight: '95vh', width: '90vw' }}>
                
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-light">
                    <h3 className="mb-0 fs-4 fw-semibold">
                        Receta Electrónica - MPR{prescriptionId}
                    </h3>
                    <div className="d-flex gap-2">
                        <button 
                            onClick={generatePDF}
                            disabled={isGenerating}
                            className={`btn ${isGenerating ? 'btn-secondary' : 'btn-success'} btn-sm d-flex align-items-center gap-2`}
                        >
                            {isGenerating ? (
                                <>
                                    <div className="spinner-border spinner-border-sm" role="status"></div>
                                    Generando PDF...
                                </>
                            ) : (
                                <>
                                    📄 Descargar PDF
                                </>
                            )}
                        </button>
                        <button onClick={onClose} className="btn btn-secondary btn-sm">
                            ✕ Cerrar
                        </button>
                    </div>
                </div>

                {/* PDF Content */}
                <div className="overflow-auto p-3" style={{ maxHeight: '80vh' }}>
                    
                    {/* Contenido para el PDF - con estilos inline para mejor compatibilidad */}
                    <div ref={pdfRef} className="pdf-content" style={{ 
                        width: '297mm', 
                        minHeight: '210mm', 
                        padding: '5mm',
                        margin: '0',
                        background: 'white',
                        fontFamily: 'Arial, sans-serif',
                        boxSizing: 'border-box',
                        position: 'relative'
                    }}>
                        
                        <style dangerouslySetInnerHTML={{ __html: customStyles }} />
                        
                        {/* Header con logos - Simplificado para PDF */}
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            marginBottom: '100px',
                            height: '50px'
                        }}>
                            <div style={{ width: '240px' }}>
                                <img src="https://imgs.search.brave.com/Z5ZQak9Cs5Spc_HCicGPWIvphjuiV5MNNi2u-dGeAR4/rs:fit:860:0:0:0/g:ce/aHR0cHM6Ly9tZWRp/YS5saWNkbi5jb20v/ZG1zL2ltYWdlL0M0/RTFCQVFFeHhSN3Ns/UTZNcmcvY29tcGFu/eS1iYWNrZ3JvdW5k/XzEwMDAwLzAvMTYw/NjgxNzMwNTYwOC9h/Y2VzaW1hX2NvdmVy/P2U9MjE0NzQ4MzY0/NyZ2PWJldGEmdD1L/YnVtWURiTTZzUVBX/UEpLcTYxRjFwdnEw/OWtiMG9KQ1JpMlRj/TkYxN2M4" 
                                     alt="ACESIMA" 
                                     style={{ maxHeight: '50%', maxWidth: '100%' }} />
                            </div>
                            <div style={{ width: '400px', textAlign: 'center' }}>
                                <img src="https://tse4.mm.bing.net/th/id/OIP.fjMS5aOEzOcSG-veA0p2UAAAAA?cb=ucfimgc2&rs=1&pid=ImgDetMain&o=7&rm=3" 
                                     alt="AINEM" 
                                     style={{ maxHeight: '50%', maxWidth: '100%' }} />
                            </div>
                            <div style={{ width: '300px', textAlign: 'right' }}>
                                <img src="https://imgs.search.brave.com/blFaDamptUQy417zAsFvXH08IWH28OJV-xWxCXOjnk8/rs:fit:860:0:0:0/g:ce/aHR0cHM6Ly9jaHNh/bHVkLmVzL2xvZ29z/L1BPTElDTElOSUNB/X01FUFJZU0EuanBn" 
                                     alt="MEPRYSA" 
                                     style={{ maxHeight: '50%', maxWidth: '100%' }} />
                            </div>

                            
                        </div>

                        {/* Título principal */}
                        <div style={{ 
                            border: '1px solid black',
                            backgroundColor: '#f8f9fa',
                            textAlign: 'center',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            padding: '8px',
                            fontSize: '12px'
                        }}>
                            RECETA MÉDICA PARA ASISTENCIA SANITARIA PRIVADA
                        </div>

                        {/* TABLA PRINCIPAL */}
                        <table style={{
                            borderCollapse: 'collapse',
                            width: '100%',
                            border: '1px solid black',
                            fontFamily: 'Arial, sans-serif',
                            fontSize: '9px',
                            tableLayout: 'fixed'
                        }}>
                            <tbody>
                                {/* FILA 1 */}
                                <tr>
                                    <td rowSpan="6" style={{
                                        border: '1px solid black',
                                        backgroundColor: '#f8f9fa',
                                        writingMode: 'vertical-rl',
                                        textOrientation: 'mixed',
                                        fontWeight: 'bold',
                                        fontSize: '10px',
                                        textAlign: 'center',
                                        letterSpacing: '3px',
                                        textTransform: 'uppercase',
                                        width: '20px',
                                        minHeight: '500px',
                                        padding: '6px'
                                    }}>
                                        RECETA MÉDICA PRIVADA
                                    </td>
                                    
                                    <td style={{
                                        border: '1px solid black',
                                        width: '250px',
                                        padding: '6px',
                                        verticalAlign: 'top'
                                    }}>
                                        <div style={{ fontWeight: 'bold', fontSize: '10px', textAlign: 'center', marginBottom: '5px' }}>
                                            PRESCRIPCIÓN
                                        </div>
                                        <div style={{ fontSize: '8px', lineHeight: '1.2' }}>
                                            Consignar el medicamento, DCI o marca, forma farmacéutica, vía de administración, dosis por unidad y unidades por envase. (Datos correspondientes a su caso de producto sanitario)
                                        </div>
                                    </td>
                                    
                                    <td style={{
                                        border: '1px solid black',
                                        width: '150px',
                                        padding: '6px',
                                        verticalAlign: 'top'
                                    }}>
                                        <div style={{ fontWeight: 'bold', fontSize: '10px', textAlign: 'center', marginBottom: '5px' }}>
                                            Duración del tratamiento
                                        </div>
                                        <div style={{ fontSize: '12px', marginTop: '10px', textAlign: 'center' }}>
                                            {treatmentDuration.value} {treatmentDuration.unit?.toLowerCase() || 'días'}
                                        </div>
                                    </td>
                                    
                                    <td style={{
                                        border: '1px solid black',
                                        width: '250px',
                                        padding: '6px',
                                        verticalAlign: 'top'
                                    }}>
                                        <div style={{ fontWeight: 'bold', fontSize: '10px', textAlign: 'center', marginBottom: '5px' }}>
                                            PACIENTE
                                        </div>
                                        <div style={{ fontSize: '8px', lineHeight: '1.2' }}>
                                            (Nombre, apellidos, año de nacimiento y nº de DNI/NIE o nº de Pasaporte)
                                        </div>
                                    </td>
                                </tr>

                                {/* FILA 2 */}
                                <tr>
                                    <td style={{
                                        border: '1px solid black',
                                        borderBottom: '1px solid black',
                                        padding: '6px',
                                        verticalAlign: 'top'
                                    }}>
                                        <div style={{ fontSize: '9px', marginTop: '10px' }}>
                                            Nº envases / unidades: 
                                            <span style={{
                                                border: '1px solid black',
                                                width: '30px',
                                                height: '25px',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: 'bold',
                                                fontSize: '12px',
                                                marginLeft: '10px',
                                                background: 'white'
                                            }}>
                                                {numberOfPackages}
                                            </span>
                                        </div>
                                    </td>
                                    
                                    <td style={{
                                        border: '1px solid black',
                                        textAlign: 'center',
                                        padding: '6px',
                                        verticalAlign: 'top'
                                    }}>
                                        <div style={{ fontWeight: 'bold', fontSize: '10px', textAlign: 'center', marginBottom: '5px' }}>
                                            Posología
                                        </div>
                                        <div style={{ fontSize: '12px', marginTop: '10px' }}>
                                            {posology.frecuencia || ''}
                                        </div>
                                        <hr />
                                        <div style={{ fontWeight: 'bold', fontSize: '10px', textAlign: 'center' }}>Unidades:</div>                                        
                                        <div style={{ fontWeight: 'bold', fontSize: '10px', textAlign: 'center' }}>Pauta:</div>
                                    </td>
                                    
                                    <td style={{
                                        border: '1px solid black',
                                        borderBottom: '1px solid black',
                                        padding: '6px',
                                        verticalAlign: 'top'
                                    }}>
                                        <div style={{ fontSize: '12px', marginBottom: '3px' }}>
                                            <strong>Nombre:</strong> {patientName}
                                        </div>
                                        <div style={{ fontSize: '12px', marginBottom: '3px' }}>
                                            <strong>DNI:</strong> {selectedPatient?.idNumber || ''}
                                        </div>
                                        <div style={{ fontSize: '12px', marginBottom: '3px' }}>
                                            <strong>F.Nac:</strong> {selectedPatient?.dateOfBirth ? formatDate(selectedPatient.dateOfBirth) : ''}
                                        </div>
                                    </td>
                                </tr>

                                {/* FILA 3 */}
                                <tr>
                                    <td style={{
                                        border: '1px solid black',
                                        borderBottom: '1px solid black',
                                        fontStyle: 'italic',
                                        fontSize: '12px',
                                        padding: '6px',
                                        verticalAlign: 'top'
                                    }}>
                                        <div style={{ fontSize: '9px', lineHeight: '1.3' }}>
                                            {medicationName && (
                                                <>
                                                    <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>
                                                        {medicationName}
                                                    </div>
                                                    <div>Principio Activo: {activeIngredient}</div>
                                                    <div>Forma: {pharmaceuticalForm}</div>
                                                    <div>Dosis: {dosage}</div>
                                                    <div>Vía: {administration}</div>
                                                    <div>Lab: {laboratory}</div>
                                                </>
                                            )}
                                        </div>                                        
                                    </td>
                                    
                                    <td style={{
                                        border: '1px solid black',
                                        padding: '6px',
                                        verticalAlign: 'top'
                                    }}>
                                        <div style={{ fontSize: '8px', marginBottom: '5px' }}>Nº. Orden dispensación</div>
                                        <div style={{ border: '1px solid black', height: '15px', background: 'white', marginBottom: '8px' }}></div>
                                        <div style={{ fontSize: '8px', marginBottom: '5px' }}>Fecha prevista dispensación</div>
                                        <div style={{ border: '1px solid black', height: '15px', background: 'white' }}></div>
                                        <hr />
                                        <div style={{ fontWeight: 'bold', fontSize: '10px', textAlign: 'center' }}>Firma de Farmacéutico</div>
                                        <div style={{ border: '1px solid black', height: '40px', background: 'white', marginTop: '5px' }}></div>
                                    </td>                                   
                                    
                                    <td style={{
                                        border: '1px solid black',
                                        padding: '6px',
                                        verticalAlign: 'top'
                                    }}>
                                        <div style={{ fontWeight: 'bold', fontSize: '10px', textAlign: 'center', marginBottom: '5px' }}>
                                            PRESCRIPTOR
                                        </div>
                                        <div style={{ fontSize: '8px', marginBottom: '8px' }}>
                                            (Nombre, apellidos, nº colegiado, especialidad, dirección, población y firma)
                                        </div>
                                        
                                        <div style={{ fontSize: '12px', marginBottom: '3px' }}>
                                            <strong>Dr.:</strong> {doctorName}
                                        </div>
                                        <div style={{ fontSize: '12px', marginBottom: '3px' }}>
                                            <strong>Nº Colegiado:</strong> {selectedDoctor?.collegiateNumber || ''}
                                        </div>
                                        <div style={{ fontSize: '12px', marginBottom: '3px' }}>
                                            <strong>Especialidad:</strong> {selectedDoctor?.specialty || ''}
                                        </div>
                                        
                                        <div style={{ fontSize: '12px', marginTop: '70px' }}>
                                            Fecha de la prescripción: {formatDate(new Date())}
                                        </div>
                                    </td>
                                </tr>

                                {/* FILA 4 */}
                                <tr>
                                    <td style={{
                                        border: '1px solid black',
                                        padding: '6px',
                                        verticalAlign: 'top'
                                    }}>
                                        Información al farmacéutico, en su caso:
                                        <div style={{ marginTop: '5px', fontStyle: 'normal' }}>{specialInstructions}</div>
                                    </td>
                                    
                                    <td style={{
                                        border: '1px solid black',
                                        padding: '6px',
                                        verticalAlign: 'top'
                                    }}>
                                        <div style={{ fontSize: '9px', fontWeight: 'bold', marginBottom: '8px' }}>Motivo de la sustitución:</div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '8px', marginBottom: '3px' }}>
                                            <input type="checkbox" style={{ width: '10px', height: '10px' }} />
                                            <span>Urgencia</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '8px', marginBottom: '3px' }}>
                                            <input type="checkbox" style={{ width: '10px', height: '10px' }} />
                                            <span>Desabastecimiento</span>
                                        </div>
                                    </td>
                                    
                                    <td style={{
                                        border: '1px solid black',
                                        padding: '6px',
                                        verticalAlign: 'top'
                                    }}>
                                        <div style={{ fontWeight: 'bold', fontSize: '10px', textAlign: 'center', marginBottom: '5px' }}>
                                            FARMACIA
                                        </div>
                                        <div style={{ fontSize: '8px', marginBottom: '8px' }}>
                                            (NIF/CIF, datos de identificación y fecha de dispensación)
                                        </div>
                                    </td>
                                </tr>

                                {/* FILA 5 */}
                                <tr>
                                    <td style={{
                                        border: '1px solid black',
                                        padding: '6px',
                                        verticalAlign: 'top',
                                        fontSize: '7px',
                                        lineHeight: '1.2',
                                        textAlign: 'justify'
                                    }}>   
                                        "La validez de esta receta expira a los 10 días naturales de la fecha prevista para la dispensación, o en su defecto de la fecha de prescripción. La medicación prescrita no dispensada en el plazo de validez de la misma no podrá ser dispensada con la misma receta. Esta receta será válida para una única dispensación y por un solo envase por principio activo del medicamento prescrito conforme a la normativa nacional."                                     
                                    </td>

                                    <td style={{
                                        border: '1px solid black',
                                        height: '30px',
                                        padding: '6px',
                                        verticalAlign: 'middle'
                                    }}>
                                        <div style={{ fontSize: '16px', fontWeight: 'bold', textAlign: 'center' }}>
                                            MPR Nº {prescriptionId}
                                        </div>
                                    </td>
                                    
                                    <td style={{
                                        border: '1px solid black',
                                        padding: '6px',
                                        verticalAlign: 'top',
                                        fontSize: '7px',
                                        lineHeight: '1.2',
                                        textAlign: 'justify'
                                    }}>
                                        En cumplimiento de lo establecido en el artículo 5 de la Ley Orgánica 15/1999, se le informa de que sus datos serán incorporados en un fichero de titularidad del prescriptor para la gestión y control de la historia clínica. Podrá ejercer sus derechos de acceso, rectificación, cancelación y oposición dirigiéndose al responsable del tratamiento para el cumplimiento de la obligación establecida en el RD 1718/2010.
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ElectronicPrescriptionPDF;